import { FC, FormEventHandler, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Loader } from 'components';
import { TextField } from 'components/inputs/text-field';
import ErrorDialog from 'components/organization/dialog/error-dialog';
import useLocationApi from 'hooks/use-location-api';
import { Location } from 'types';
import { formatLocation } from 'utils/format';
import StateSelect from '../state';
import TimeZoneSelect from '../time-zone';

const EditLocation: FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { getLocation, validateCodeUniqueness, editLocation } = useLocationApi();

  const [loading, setLoading] = useState<boolean>(false);
  const [location, setLocation] = useState<Location | null>();

  useEffect(() => {
    if (!isNaN(Number(params.id))) {
      setLoading(true);
      getLocation(Number(params.id))
        .then(setLocation)
        .finally(() => setLoading(false));
    }
  }, [params.id, getLocation, setLocation]);

  const handleReset: FormEventHandler = useCallback(
    e => {
      e.preventDefault();
      navigate(`/settings/locations/${params.id}`, { replace: true });
    },
    [params.id, navigate]
  );

  const handleSubmit: FormEventHandler = useCallback(
    async e => {
      e.preventDefault();
      if (!location) return;
      const result = await editLocation(location);
      if (result) navigate(`/settings/locations/${params.id}`, { replace: true });
    },
    [editLocation, location, params.id, navigate]
  );

  return (
    <Loader loading={loading}>
      <form
        className="page"
        id="edit-location"
        onReset={handleReset}
        onSubmit={handleSubmit}
        data-cy="edit-location-form"
      >
        <div className="page-header">
          <h1>{formatLocation(location) || 'N/A'}</h1>
          <div className="grid-2">
            <button className="btn-outline" type="reset">
              CANCEL
            </button>
            <button className="btn-primary" type="submit">
              SUBMIT
            </button>
          </div>
        </div>
        <div className="card-primary">
          <div className="card-header mb-5" />
          <div className="card-body">
            <div className="grid-3 p-2" style={{ maxWidth: 1200 }}>
              <TextField
                id="location-name"
                name="name"
                label="NAME"
                value={location?.name ?? ''}
                onChange={name => setLocation(prevState => prevState && { ...prevState, name })}
                required
                allowSpaces
                data-cy="edit-location-name-input"
              />
              <TextField
                id="location-code"
                name="code"
                label="CODE"
                value={location?.code ?? ''}
                onChange={code => setLocation(prevState => prevState && { ...prevState, code })}
                validate={validateCodeUniqueness}
                required
                allowSpaces
                data-cy="edit-location-code-input"
              />
              <TimeZoneSelect
                timeZone={location?.timeZone}
                setTimeZone={timeZone => setLocation(prevState => prevState && { ...prevState, timeZone })}
              />
              <TextField
                id="location-address"
                name="address"
                label="ADDRESS"
                value={location?.address ?? ''}
                onChange={address => setLocation(prevState => prevState && { ...prevState, address })}
                required
                allowSpaces
                data-cy="edit-location-address-input"
              />
              <TextField
                id="location-address-2"
                name="address2"
                label="ADDRESS 2"
                value={location?.address2 ?? ''}
                onChange={address2 => setLocation(prevState => prevState && { ...prevState, address2 })}
                allowSpaces
                data-cy="edit-location-address-2-input"
              />
              <TextField
                id="location-zip-code"
                name="zipCode"
                label="ZIP CODE"
                inputMode="numeric"
                value={location?.zipCode ?? ''}
                onChange={zipCode => setLocation(prevState => prevState && { ...prevState, zipCode })}
                required
                data-cy="edit-location-zip-code-input"
              />
              <TextField
                id="location-city"
                name="city"
                label="CITY"
                value={location?.city ?? ''}
                onChange={city => setLocation(prevState => prevState && { ...prevState, city })}
                required
                allowSpaces
                data-cy="edit-location-city-input"
              />
              <StateSelect
                state={location?.state}
                setState={state => setLocation(prevState => prevState && { ...prevState, state })}
              />
              <TextField
                id="location-country"
                name="country"
                label="COUNTRY"
                value={location?.country ?? ''}
                onChange={country => setLocation(prevState => prevState && { ...prevState, country })}
                required
                allowSpaces
                data-cy="edit-location-country-input"
              />
              <TextField
                id="location-remit-to"
                name="remitTo"
                label="REMIT TO"
                value={location?.remitTo ?? ''}
                onChange={remitTo => setLocation(prevState => prevState && { ...prevState, remitTo })}
                allowSpaces
                data-cy="edit-location-remit-to-input"
              />
              <div className="text-field">
                <input
                  type="checkbox"
                  checked={!!location?.invoiceRemitToDisplayed}
                  onChange={e =>
                    setLocation(prevState => prevState && { ...prevState, invoiceRemitToDisplayed: e.target.checked })
                  }
                  style={{ margin: '8px 16px 0', outline: 'none', width: 16 }}
                  data-cy="edit-location-remit-to-checkbox"
                />
                <label>INVOICE REMIT TO</label>
              </div>
            </div>
          </div>
        </div>
      </form>
      <ErrorDialog />
    </Loader>
  );
};

export default EditLocation;
